import React from 'react';
import { useTheme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

// Map invisible characters to visible glyphs so users can see whitespace they
// authored or pasted. The original character stays in the underlying value —
// the glyph is purely display.
const GLYPH_BY_CHAR = new Map<string, string>([
  ['\x20', '·'], // SPACE          → MIDDLE DOT (·)
  ['\u00A0', '␣'], // NO-BREAK SPACE → OPEN BOX (␣)
  ['\t', '→'], // TAB            → RIGHTWARDS ARROW (→)
  ['\n', '↵'], // LF             → DOWNWARDS ARROW WITH CORNER LEFTWARDS (↵)
  ['\r', '↩'], // CR             → LEFTWARDS ARROW WITH HOOK (↩)
]);

// Any remaining C0 control character (U+0000–U+001F) or DEL (U+007F).
// eslint-disable-next-line no-control-regex
const OTHER_CONTROL_RE = /[\x00-\x1F\x7F]/;
const CONTROL_FALLBACK_GLYPH = '▯'; // WHITE VERTICAL RECTANGLE (▯)

interface VisibleToken {
  ch: string;
  glyph?: string;
}

const classify = (ch: string): VisibleToken => {
  const glyph = GLYPH_BY_CHAR.get(ch);
  if (glyph !== undefined) {
    return { ch, glyph };
  }
  if (OTHER_CONTROL_RE.test(ch)) {
    return { ch, glyph: CONTROL_FALLBACK_GLYPH };
  }
  return { ch };
};

const tokenize = (text: string): VisibleToken[] => Array.from(text, classify);

interface WhitespaceTextProps {
  value: string;
  glyphColor: string;
}

// Display-only rendering of a string with whitespace and control characters
// replaced by visible glyphs (subdued color). Plain characters render
// unchanged. Pairs with `useQuoteStyle` for the surrounding curly-quote chrome.
//
// Renders glyphs as plain inline <span> (not pounceV2 Box) so text-overflow
// ellipsis on a parent <Text truncated> keeps working — inline-block siblings
// would break the single-line text flow that ellipsis depends on.
export const WhitespaceText = ({ value, glyphColor }: WhitespaceTextProps) => {
  const tokens = React.useMemo(() => tokenize(value), [value]);
  return (
    <>
      {tokens.map((t, i) =>
        t.glyph !== undefined ? (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} style={{ color: glyphColor }} aria-hidden="true">
            {t.glyph}
          </span>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i}>{t.ch}</React.Fragment>
        ),
      )}
    </>
  );
};

// Curly-quote decoration style used everywhere a filter value renders so
// boundaries are visually unambiguous. Cursor is intentionally not baked in —
// callers that need a `not-allowed` cursor (e.g. the disabled-input chrome)
// add it via their own sx alongside this style.
export const useQuoteStyle = (): SystemStyleObject => {
  const {
    palette: { tokens },
  } = useTheme();
  return React.useMemo(
    () => ({
      color: tokens.content.assets.semantic.suggest.base,
      fontWeight: 700,
      fontFamily: 'Georgia, serif',
      fontSize: '16px',
      lineHeight: 1,
    }),
    [tokens.content.assets.semantic.suggest.base],
  );
};

export const useGlyphColor = (): string => {
  const {
    palette: { tokens },
  } = useTheme();
  return tokens.content.text.neutral[3];
};

// Shared sx for inputs that render their text transparently so a glyph overlay
// behind them is the visible representation. Keeps caret + selection visible
// while the input layer itself contributes only chrome (placeholder, focus
// ring, etc.). Used by TagsValueInputV3 (per-slot) and QuotedTextInputV3.
export const useTransparentMonoInputSx = (): SystemStyleObject => {
  const {
    palette: { tokens, componentTokens },
  } = useTheme();
  const inputTextColor = componentTokens.tags.colors.condition.text.labelBase;
  const placeholderColor = componentTokens.tags.colors.condition.text.staticBase;
  const selectionBg = tokens.background.neutral.raised[1];
  return React.useMemo(
    () => ({
      outline: 'none',
      caretColor: inputTextColor,
      '&::placeholder': {
        color: placeholderColor,
        opacity: 0.4,
        WebkitTextFillColor: placeholderColor,
      },
      '&::selection': {
        backgroundColor: selectionBg,
        color: 'transparent',
      },
    }),
    [inputTextColor, placeholderColor, selectionBg],
  );
};

export { default as QuotedTextInputV3 } from './QuotedTextInputV3';
export type { QuotedTextInputProps } from './QuotedTextInputV3';
